<template>
  <div v-if="product" class="detail">
    <router-link class="detail__back" :to="typePath">← 返回 {{ typeName }}</router-link>

    <header class="detail__head panel">
      <div class="detail__badges">
        <span class="tag" :class="product.paid ? 'is-paid' : 'is-free'">
          {{ product.paid ? '付费' : '免费' }}
        </span>
        <span class="badge" :class="'badge-' + product.method.toLowerCase()">{{ product.method }}</span>
        <span class="mono path">{{ product.path }}</span>
      </div>
      <h1 class="page-title" style="margin-bottom: 8px">{{ product.title }}</h1>
      <p class="page-desc" style="margin-bottom: 12px">{{ product.desc }}</p>
      <div class="detail__price">
        <span>{{ product.limitLabel }}</span>
        <strong>{{ product.priceLabel }}</strong>
      </div>
      <div class="detail__actions">
        <router-link class="btn btn-primary" :to="{ path: '/playground', query: { id: product.id } }">
          在线调试
        </router-link>
        <router-link class="btn btn-ghost" to="/keys">获取密钥</router-link>
      </div>
    </header>

    <section class="panel detail__body">
      <h2 class="h">接口说明</h2>
      <p class="mono base">{{ fullUrl }}</p>
      <p v-if="product.controller" class="meta mono">控制器：{{ product.controller }}</p>

      <h2 class="h">参数</h2>
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>名称</th>
              <th>位置</th>
              <th>类型</th>
              <th>必填</th>
              <th>说明</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in product.params" :key="p.name + p.in">
              <td class="mono">{{ p.name }}</td>
              <td>{{ p.in }}</td>
              <td class="mono">{{ p.type }}</td>
              <td>{{ p.required ? '是' : '否' }}</td>
              <td>{{ p.desc }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <template v-if="product.sampleBody">
        <h2 class="h">请求示例</h2>
        <pre class="mono">{{ pretty(product.sampleBody) }}</pre>
      </template>

      <h2 class="h">响应示例</h2>
      <pre class="mono">{{ pretty(product.sampleResponse) }}</pre>
    </section>
  </div>
  <div v-else class="panel empty">
    未找到该产品
    <router-link to="/type/all">返回所有产品</router-link>
  </div>
</template>

<script>
import { API_BASE, getProductById, getTypeById, resolveRequestUrl } from '../data/apiCatalog'
import { applyPricingToProduct, loadOpenGatePricing } from '../utils/opengatePricing'

export default {
  name: 'ProductDetail',
  data() {
    return { pricing: null }
  },
  computed: {
    product() {
      const base = getProductById(this.$route.params.id)
      return applyPricingToProduct(base, this.pricing)
    },
    typeName() {
      if (!this.product) return '所有产品'
      return getTypeById(this.product.typeId).name
    },
    typePath() {
      if (!this.product) return '/type/all'
      return getTypeById(this.product.typeId).path
    },
    fullUrl() {
      if (!this.product) return ''
      const path = resolveRequestUrl(this.product.path, this.product.samplePathParams || {})
      if (path.startsWith('http')) return path
      return (API_BASE || 'https://open.pluskeji.cn') + path
    }
  },
  created() {
    loadOpenGatePricing()
      .then(p => {
        this.pricing = p
      })
      .catch(() => {
        this.pricing = null
      })
  },
  methods: {
    pretty(obj) {
      return JSON.stringify(obj, null, 2)
    }
  }
}
</script>

<style scoped>
.detail__back {
  display: inline-block;
  margin-bottom: 14px;
  font-size: 13px;
  color: var(--muted);
}
.detail__back:hover {
  color: var(--accent);
}
.detail__head {
  padding: 22px 20px;
  margin-bottom: 16px;
}
.meta {
  margin: 6px 0 0;
  font-size: 12px;
  color: #64748b;
}
.meta a {
  color: #0f766e;
}
.detail__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;
}
.tag {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
}
.tag.is-paid {
  color: #ffc857;
  background: rgba(255, 200, 87, 0.12);
}
.tag.is-free {
  color: var(--accent);
  background: rgba(125, 255, 179, 0.12);
}
.path {
  font-size: 12px;
  color: var(--muted);
}
.detail__price {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 16px;
}
.detail__price strong {
  color: var(--accent);
}
.detail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.detail__body {
  padding: 20px;
}
.h {
  margin: 20px 0 10px;
  font-size: 13px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}
.base {
  margin: 0;
  font-size: 13px;
  color: var(--accent);
}
.table-wrap {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
th,
td {
  padding: 8px 10px;
  border-bottom: 1px solid var(--line);
  text-align: left;
}
th {
  color: var(--muted);
  font-size: 11px;
  text-transform: uppercase;
}
pre {
  margin: 0;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: rgba(0, 0, 0, 0.35);
  font-size: 12.5px;
  line-height: 1.55;
  overflow-x: auto;
}
.empty {
  padding: 40px;
  text-align: center;
  color: var(--muted);
}
.empty a {
  display: block;
  margin-top: 12px;
  color: var(--accent);
}
</style>
